import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Navbar from "../../components/shared/Navbar";
import NavbarWeb from "../../components/NavbarWeb";

const validationSchema = Yup.object({
  firstname: Yup.string()
    .required("Firstname is required")
    .max(100, "Limit exceeded"),
  lastname: Yup.string()
    .required("Lastname is required")
    .max(100, "Limit exceeded"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email")
    .max(100, "Limit exceeded"),
  message: Yup.string()
    .required("Message is required")
    .max(200, "Limit exceeded"),
});

function ContactForm() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const initialValues = {
    firstname: "",
    lastname: "",
    email: "",
    message: "",
  };

  const { values, errors, handleChange, handleSubmit, touched, resetForm } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values, { resetForm }) => {
        setLoading(true);
        setStatus(null);
        try {
          const payload = {
            name: `${values.firstname} ${values.lastname}`,
            email: values.email,
            message: values.message,
          };

          await axios.post(
            `${process.env.REACT_APP_BASE_URL}/api/contact-us/`,
            payload
          );

          setStatus("success");
          resetForm();
        } catch (err) {
          console.error("Error sending message:", err);
          setStatus("error");
        } finally {
          setLoading(false);
        }
      },
    });

  return (
    <div className="w-full min-h-screen px-4">
      <NavbarWeb />

      <div className="flex justify-center mt-8">
        <div className="w-full max-w-[700px]">
          <h2 className="text-[36px] font-[900] text-center text-[#19462F]">
            Contact Us
          </h2>
          <p className="text-center text-green-600 mt-3 text-sm md:text-base">
            Have something in mind you need help with? We’d love to hear your
            thoughts!
          </p>

          {/* Status Messages */}
          {status === "success" && (
            <div className="mt-6 rounded-lg bg-green-100 border border-green-300 text-green-800 px-4 py-3 text-sm font-medium">
              Message sent successfully! We’ll get back to you soon.
            </div>
          )}
          {status === "error" && (
            <div className="mt-6 rounded-lg bg-red-100 border border-red-300 text-red-800 px-4 py-3 text-sm font-medium">
              Failed to send message. Please try again later.
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-green-700 font-semibold mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="Input your first name in here"
                  name="firstname"
                  value={values.firstname}
                  onChange={handleChange}
                  className="w-full h-[48px] px-4 rounded-[10px] bg-[#F5F7F8] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                />
                {errors.firstname && touched.firstname && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.firstname}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-green-700 font-semibold mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Input your last name in here"
                  name="lastname"
                  value={values.lastname}
                  onChange={handleChange}
                  className="w-full h-[48px] px-4 rounded-[10px] bg-[#F5F7F8] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                />
                {errors.lastname && touched.lastname && (
                  <p className="text-red-700 text-xs mt-1">{errors.lastname}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-green-700 font-semibold mb-1">
                Email Address
              </label>
              <input
                type="email"
                placeholder="Input your email address in here"
                name="email"
                value={values.email}
                onChange={handleChange}
                className="w-full h-[48px] px-4 rounded-[10px] bg-[#F5F7F8] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
              />
              {errors.email && touched.email && (
                <p className="text-red-700 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-green-700 font-semibold mb-1">
                Messages
              </label>
              <textarea
                rows={5}
                placeholder="Write your messages in here"
                name="message"
                value={values.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-[10px] bg-[#F5F7F8] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
              ></textarea>
              {errors.message && touched.message && (
                <p className="text-red-700 text-xs mt-1">{errors.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-3 font-semibold rounded-full shadow-md outline-none focus:ring-2 focus:ring-offset-1 ${
                  loading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-primary text-white hover:bg-green-700"
                }`}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;
